import {
    getEntraAuthority,
    getEntraClientId,
    getMcpApiAppId,
    getMcpApiIdentifierUri,
    getMcpClientId,
} from "@/lib/auth/config";

export type EntraMsalConfig = {
    clientId: string;
    authority: string;
    knownAuthorities: string[];
};

function authorityHost(authority: string): string {
    try {
        return new URL(authority).hostname;
    } catch {
        return authority.replace(/^https?:\/\//, "").split("/")[0] ?? authority;
    }
}

/** Shared MSAL configuration for browser + server (B2.11). */
export function getEntraMsalConfig(): EntraMsalConfig | null {
    const clientId = getEntraClientId();
    const authority = getEntraAuthority();
    if (!clientId || !authority) return null;

    const host = authorityHost(authority);
    return {
        clientId,
        authority: authority.endsWith("/") ? authority.slice(0, -1) : authority,
        knownAuthorities: [host],
    };
}

/** OpenID metadata JWKS URI for Entra External ID / CIAM tenants. */
export function getEntraJwksUri(authority: string): string {
    const base = authority.endsWith("/") ? authority.slice(0, -1) : authority;
    return `${base}/discovery/v2.0/keys`;
}

/**
 * Returns the issuer(s) the id_token's `iss` claim is allowed to have.
 *
 * CIAM (Entra External ID) accepts two equivalent forms of the authority URL:
 *   1. Tenant-ID host:  https://<tenantId>.ciamlogin.com/<tenantId>
 *   2. Custom subdomain: https://<subdomain>.ciamlogin.com/<tenantId>
 *
 * The id_token's issuer is *always* the tenant-ID host form regardless of
 * which authority form the client used to log in. To make server-side
 * validation tolerant of either authority configuration, we return BOTH
 * possible forms and let `jose.jwtVerify` accept whichever matches.
 *
 * jose accepts `issuer: string | string[]` in its options.
 */
export function getEntraIssuer(authority: string): string | string[] {
    const base = authority.endsWith("/") ? authority.slice(0, -1) : authority;
    const issuers = new Set<string>([`${base}/v2.0`]);

    try {
        const u = new URL(base);
        const tenantId = u.pathname.split("/").filter(Boolean)[0];
        if (tenantId && u.hostname.endsWith(".ciamlogin.com")) {
            const canonicalHost = `${tenantId}.ciamlogin.com`;
            const canonical = `https://${canonicalHost}/${tenantId}/v2.0`;
            issuers.add(canonical);
        }
    } catch {
        // authority isn't a parseable URL — fall back to the base-derived issuer.
    }

    return issuers.size === 1
        ? Array.from(issuers)[0]
        : Array.from(issuers);
}

export function getMcpApiAudience(): string | null {
    return getMcpApiIdentifierUri() ?? getMcpApiAppId() ?? null;
}

export type McpApiConfig = {
    appId: string | null;
    identifierUri: string | null;
    audience: string | null;
    clientId: string | null;
    authority: string | null;
};

/** Server-side MCP API + client credentials config (KF-16). */
export function getMcpApiConfig(): McpApiConfig | null {
    const authority = getEntraAuthority() ?? null;
    const appId = getMcpApiAppId() ?? null;
    const identifierUri = getMcpApiIdentifierUri() ?? null;
    const audience = getMcpApiAudience();
    if (!authority || !audience) return null;
    return {
        appId,
        identifierUri,
        audience,
        clientId: getMcpClientId() ?? null,
        authority: authority.endsWith("/") ? authority.slice(0, -1) : authority,
    };
}
